import type { ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import Filters from "byzantine/src/filters";

type Thread = {
  threadId: string;
  subject: string;
  updatedAt?: string;
  isRead?: boolean;
};

type EmptyThreadItemProps = {
  subject: ReactNode;
  subtext?: ReactNode;
};

function EmptyThreadItem({ subject, subtext }: EmptyThreadItemProps) {
  return (
    <div className="thread-item">
      <div className="thread-item-title">{subject}</div>
      <div>{subtext}</div>
    </div>
  );
}

type ThreadItemProps = {
  threadId: string;
  selected?: boolean;
  subject: string;
  updatedAt?: string;
  onSelected?: (threadId: string) => void;
  alternativeMessage?: string;
  isRead?: boolean;
};

function ThreadItem({
  threadId,
  selected,
  subject,
  updatedAt,
  onSelected,
  alternativeMessage,
  isRead,
}: ThreadItemProps) {
  const threadItemClasses = selected ? "thread-item selected" : "thread-item";

  return (
    <div
      role="button"
      tabIndex={0}
      className={threadItemClasses}
      onKeyUp={({ key }) => {
        if (onSelected && key === "Enter") {
          onSelected(threadId);
        }
      }}
      onClick={() => {
        if (onSelected) {
          onSelected(threadId);
        }
      }}
    >
      {!isRead ? (
        <div className="thread-read-indicator">
          <span className="narmi-icon-solid-circle thread-read-indicator-icon" />
        </div>
      ) : null}

      <div className="thread-item-title">{subject}</div>
      {updatedAt ? (
        <div>{Filters.longMonthDayYear(updatedAt)}</div>
      ) : (
        <div>{alternativeMessage}</div>
      )}
    </div>
  );
}

type ThreadListProps = {
  threads: Thread[] | null;
  selectedThreadId?: string | null;
  onThreadSelected: (threadId: string) => void;
  emptyListSubject?: ReactNode;
  emptyListSubtext?: ReactNode;
};

export default function ThreadList({
  threads,
  selectedThreadId,
  onThreadSelected,
  emptyListSubject,
  emptyListSubtext,
}: ThreadListProps) {
  const { l10n } = useLocalization();

  if (threads === null) {
    return <div className="thread-list"></div>;
  }

  if (threads.length === 0) {
    return (
      <div className="thread-list">
        <EmptyThreadItem
          subject={emptyListSubject || l10n.getString("thread-list-empty-state")}
          subtext={emptyListSubtext}
        />
      </div>
    );
  }

  return (
    <div className="thread-list">
      {threads.map(({ threadId, subject, updatedAt, isRead }) => (
        <ThreadItem
          key={threadId}
          threadId={threadId}
          subject={subject}
          updatedAt={updatedAt}
          selected={threadId === selectedThreadId}
          onSelected={(newSelectedThreadId) =>
            onThreadSelected(newSelectedThreadId)
          }
          isRead={isRead}
        />
      ))}
    </div>
  );
}
